<script>
import Form from '../Form.vue';
import Sortable from 'sortablejs';
import axios from 'axios';
import OnlinePlannerEmployee from '../OnlinePlanner/Employee.vue';

export default {
    extends: Form,

    components: {
        'frm-online-planner-employee': OnlinePlannerEmployee,
    },

    props: {
        physitrackCreateTokenUrl: {
            type: String,
            default: null,
        },
        physitrackDeleteTokenUrl: {
            type: String,
            default: null,
        },
        agbCodes: {
            type: Array,
            required: true,
        },
        caregiverSpecificationCategories: {
            type: Array,
            required: true,
        },
        caregiverSpecificationsGrouped: {
            type: Object,
            required: true,
        },
        multipleEmployeeAgbsEnabled: {
            type: Boolean,
            default: false,
        },
        initialEmployeeAgbs: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            physitrackTokenCopied: false,
            physitrackTokenLoading: false,
            complaintAreaSelectKey: 0,
        };
    },

    computed: {
        mentorAgbs() {
            const mentorId = this.formData.mentor ? String(this.formData.mentor) : null;

            if (!mentorId) {
                return [];
            }

            const mentor = this.agbCodes.find((a) => String(a.id) === mentorId);

            if (!mentor) {
                return [];
            }

            if (Array.isArray(mentor.agbs) && mentor.agbs.length) {
                return mentor.agbs.map(agb => this.normalizeAgbSummary(agb));
            }

            return mentor.agb ? [this.normalizeAgbSummary(mentor.agb)] : [];
        },
    },

    watch: {
        'formData.function'() {
            this.$nextTick(() => this.initEmployeeAgbSortable());
        },
    },

    mounted() {
        if (!this.multipleEmployeeAgbsEnabled) {
            return;
        }

        const employeeAgbs = this.initialEmployeeAgbs.length
            ? this.initialEmployeeAgbs.map(employeeAgb => this.normalizeEmployeeAgb(employeeAgb))
            : [this.createEmployeeAgb()];

        this.$set(this.formData, 'employee_agbs', employeeAgbs);
        this.setOriginalFormData(this.formData);

        this.$nextTick(() => {
            this.initEmployeeAgbSortable();
        });
    },

    methods: {
        syncOnlinePlannerComplaintAreas(employee) {
            this.$set(this.formData, 'complaint_area_ids', [...employee.complaint_area_ids]);
            this.complaintAreaSelectKey++;
        },

        initEmployeeAgbSortable() {
            if (!this.multipleEmployeeAgbsEnabled || !this.$refs.employeeAgbRows || Sortable.get(this.$refs.employeeAgbRows)) {
                return;
            }

            Sortable.create(this.$refs.employeeAgbRows, {
                handle: '.employee-agb-row__handle',
                animation: 150,
                ghostClass: 'employee-agb-row--ghost',
                // Native HTML5 drag-and-drop can't be triggered by Selenium's mouse-based
                // drag simulation, so use Sortable's own mouse-event-driven drag instead.
                forceFallback: true,
                onEnd: (event) => this.reorderEmployeeAgbs(event.oldIndex, event.newIndex),
            });
        },

        reorderEmployeeAgbs(oldIndex, newIndex) {
            const employeeAgbs = [...this.formData.employee_agbs];
            const [movedEmployeeAgb] = employeeAgbs.splice(oldIndex, 1);
            employeeAgbs.splice(newIndex, 0, movedEmployeeAgb);

            this.formData.employee_agbs = employeeAgbs;
        },

        addEmployeeAgb() {
            this.formData.employee_agbs = [
                ...this.formData.employee_agbs,
                this.createEmployeeAgb(),
            ];
        },

        removeEmployeeAgb(index) {
            if (this.formData.employee_agbs.length === 1) {
                this.formData.employee_agbs = this.formData.employee_agbs.map((employeeAgb, employeeAgbIndex) => (
                    employeeAgbIndex === index ? this.clearEmployeeAgb(employeeAgb) : employeeAgb
                ));

                return;
            }

            this.formData.employee_agbs = this.formData.employee_agbs
                .filter((_, employeeAgbIndex) => employeeAgbIndex !== index);
        },

        clearEmployeeAgb(employeeAgb) {
            return {
                ...employeeAgb,
                agb: '',
                caregiver_specification_category_id: '',
                caregiver_specification_id: '',
            };
        },

        createEmployeeAgb() {
            return this.normalizeEmployeeAgb({
                id: '',
                agb: '',
                caregiver_specification_category_id: '',
                caregiver_specification_id: '',
            });
        },

        normalizeEmployeeAgb(employeeAgb) {
            const caregiverSpecificationId = String(employeeAgb.caregiver_specification_id || '');

            return {
                id: employeeAgb.id || '',
                agb: employeeAgb.agb || '',
                caregiver_specification_category_id: String(employeeAgb.caregiver_specification_category_id ||
                    this.getCaregiverSpecificationCategoryId(caregiverSpecificationId) ||
                    ''),
                caregiver_specification_id: caregiverSpecificationId,
            };
        },

        categoryIdForAgb(agb) {
            const prefix = String(agb || '').slice(0, 2);
            if (prefix.length < 2) {
                return '';
            }
            const match = this.caregiverSpecificationCategories
                .find(category => String(category.label).split(' ')[0] === prefix);
            return match ? String(match.value) : '';
        },

        autofillEmployeeAgbCategory(employeeAgb, finalize = false) {
            const prefix = String(employeeAgb.agb || '').slice(0, 2);
            if (prefix.length < 2 && !finalize) {
                return;
            }

            employeeAgb.caregiver_specification_category_id = this.categoryIdForAgb(employeeAgb.agb);
            this.resetEmployeeAgbSpecificationIfUnavailable(employeeAgb);
        },

        caregiverSpecificationsForEmployeeAgb(employeeAgb) {
            const categoryId = String(employeeAgb.caregiver_specification_category_id || '');

            if (!categoryId) {
                return [{ value: '', label: '' }];
            }

            return [
                { value: '', label: '' },
                ...(this.caregiverSpecificationsGrouped[categoryId] || []),
            ];
        },

        getCaregiverSpecificationCategoryId(caregiverSpecificationId) {
            const specificationId = String(caregiverSpecificationId || '');

            if (!specificationId) {
                return null;
            }

            return Object.keys(this.caregiverSpecificationsGrouped)
                .find(categoryId => this.caregiverSpecificationsGrouped[categoryId]
                    .some(specification => String(specification.value) === specificationId));
        },

        resetEmployeeAgbSpecificationIfUnavailable(employeeAgb) {
            const specificationAvailable = this.caregiverSpecificationsForEmployeeAgb(employeeAgb)
                .some(specification => String(specification.value || '') === String(employeeAgb.caregiver_specification_id || ''));

            if (!specificationAvailable) {
                employeeAgb.caregiver_specification_id = '';
            }
        },

        normalizeAgbSummary(summary) {
            if (typeof summary === 'string') {
                return {
                    agb: summary,
                    caregiver_specification: '',
                    summary,
                };
            }

            return {
                agb: summary.agb || '',
                caregiver_specification: summary.caregiver_specification || '',
                summary: summary.summary || '',
            };
        },

        async physitrackCopyToken() {
            await navigator.clipboard.writeText(this.formData.physitrack_token);
            this.physitrackTokenCopied = true;
            setTimeout(() => (this.physitrackTokenCopied = false), 5 * 1000);
        },

        async physitrackGenerateToken() {
            this.physitrackTokenLoading = true;

            this.formData.physitrack_token = (await axios.post(this.physitrackCreateTokenUrl)).data;

            this.physitrackTokenCopied = false;
            this.physitrackTokenLoading = false;
        },

        async physitrackDeleteToken() {
            this.physitrackTokenLoading = true;

            await axios.delete(this.physitrackDeleteTokenUrl);

            this.formData.physitrack_token = '';
            this.physitrackTokenCopied = false;
            this.physitrackTokenLoading = false;
        },
    },
};
</script>
